/**
 * Last start time per synth. Two eggs can fire in the same moment (winning the
 * sheep run triggers 'sheep-home', and with it possibly the hunt's completion
 * payout) - playing the same synth twice a few milliseconds apart phases
 * against itself and sounds broken, so the second call is dropped.
 */
const lastPlayedAt: Record<string, number> = {}
const duplicateWindowMs = 700

/**
 * One reused <audio> element per clip URL. iOS Safari unlocks audio per-element
 * on a play() that happens inside a user gesture; a *new* Audio() created later
 * (e.g. from a devicemotion shake, which is not a gesture) starts locked and is
 * silently blocked. Creating a fresh element every trigger is exactly why the
 * clip played once and then went quiet on iPhone. Reusing one element - unlocked
 * once via primeSound() during the arm tap, or on its first successful play -
 * lets it replay for the rest of the page's life.
 */
const audioCache: Record<string, HTMLAudioElement> = {}

function getAudio(src: string): HTMLAudioElement {
  let audio = audioCache[src]
  if (!audio) {
    audio = new Audio(src)
    audio.preload = 'auto'
    audioCache[src] = audio
  }
  return audio
}

/**
 * Unlock a clip for later gesture-less playback (iOS). Call from inside a real
 * user gesture (e.g. the footer-logo arm tap): a muted play()/pause() marks the
 * element as user-initiated so a later shake can replay it without a gesture.
 */
export function primeSound(src: string) {
  const audio = getAudio(src)
  const restore = audio.volume
  audio.volume = 0
  audio
    .play()
    .then(() => {
      audio.pause()
      audio.currentTime = 0
      audio.volume = restore
    })
    .catch(() => {
      audio.volume = restore
    })
}

export function playSound(options: {
  src?: string
  volume?: number
  synth?: 'chime' | 'pop' | 'fanfare'
  // for src clips only: fade the volume down to silence over the last this-many
  // ms instead of the clip cutting off abruptly at its end
  fadeOutMs?: number
}) {
  const { src, volume = 0.4, synth = 'chime', fadeOutMs = 0 } = options

  const key = src ?? synth
  const now = Date.now()
  if (now - (lastPlayedAt[key] ?? 0) < duplicateWindowMs) return
  lastPlayedAt[key] = now

  if (src) {
    const audio = getAudio(src)
    audio.volume = volume
    // restart from the top - the element is reused across triggers
    audio.currentTime = 0

    if (fadeOutMs > 0) {
      const fadeSec = fadeOutMs / 1000
      // ramp the volume down over the final fadeSec so the clip eases out
      // rather than stopping dead. rAF keeps it smooth; duration is NaN until
      // metadata loads, which the isFinite guard rides out.
      const step = () => {
        if (audio.paused || audio.ended) return
        const remaining = audio.duration - audio.currentTime
        if (Number.isFinite(remaining) && remaining < fadeSec) {
          audio.volume = Math.max(0, volume * (remaining / fadeSec))
        }
        requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    }

    audio.play().catch(() => {
      // autoplay blocked (no prior user gesture) - the egg stays silent
    })
    return
  }

  // Synthesized in the browser on purpose - these ARE the final sounds,
  // so no audio asset needs to be bundled.
  if (typeof AudioContext === 'undefined') return
  const ctx = new AudioContext()
  const play =
    synth === 'pop' ? playPop : synth === 'fanfare' ? playFanfare : playChime

  const start = () => {
    const lifetimeMs = play(ctx, volume)
    setTimeout(() => {
      ctx.close().catch(() => {})
    }, lifetimeMs)
  }

  if (ctx.state === 'suspended') {
    ctx
      .resume()
      .then(start)
      .catch(() => {})
  } else {
    start()
  }
}

// A soft C-major pad ("oooh"). Returns its lifetime in ms.
function playChime(ctx: AudioContext, volume: number): number {
  const master = ctx.createGain()
  master.connect(ctx.destination)

  const now = ctx.currentTime
  master.gain.setValueAtTime(0, now)
  master.gain.linearRampToValueAtTime(volume, now + 0.4)
  master.gain.setValueAtTime(volume, now + 1.4)
  master.gain.linearRampToValueAtTime(0, now + 2.8)

  const frequencies = [261.63, 329.63, 392, 523.25]
  for (const frequency of frequencies) {
    const oscillator = ctx.createOscillator()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    const gain = ctx.createGain()
    gain.gain.value = 1 / frequencies.length
    oscillator.connect(gain)
    gain.connect(master)
    oscillator.start(now)
    oscillator.stop(now + 3)
  }

  return 3200
}

/**
 * A little bell arpeggio for a win: C-E-G-C struck one after the other, each
 * with a hard attack and its own decay, plus a soft octave sparkle on top.
 * Unlike playChime this never holds a chord - a sustained sine cluster is what
 * makes a synth sound like a dial tone. Returns its lifetime in ms.
 */
function playFanfare(ctx: AudioContext, volume: number): number {
  const start = ctx.currentTime
  // C5 E5 G5 C6, then the fifth above as a sparkle
  const notes = [523.25, 659.25, 783.99, 1046.5, 1567.98]
  const step = 0.11
  const decay = 0.9

  notes.forEach((frequency, index) => {
    const at = start + index * step
    // last note rings out longer, the sparkle sits quieter in the mix
    const isSparkle = index === notes.length - 1
    const peak = volume * (isSparkle ? 0.18 : 0.34)
    const length = isSparkle ? decay * 1.6 : decay

    // two slightly detuned voices per note give it a bell-like shimmer that a
    // single oscillator cannot produce
    for (const detune of [-4, 4]) {
      const oscillator = ctx.createOscillator()
      oscillator.type = 'triangle'
      oscillator.frequency.value = frequency
      oscillator.detune.value = detune

      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0, at)
      gain.gain.linearRampToValueAtTime(peak / 2, at + 0.012)
      // exponential decay, never to exactly 0 - that would throw
      gain.gain.exponentialRampToValueAtTime(0.0001, at + length)

      oscillator.connect(gain)
      gain.connect(ctx.destination)
      oscillator.start(at)
      oscillator.stop(at + length + 0.05)
    }
  })

  return Math.round((notes.length * step + decay * 1.6 + 0.2) * 1000)
}

// A party-popper "pop": short noise burst plus a low thump. Returns its
// lifetime in ms.
function playPop(ctx: AudioContext, volume: number): number {
  const now = ctx.currentTime

  const noiseDuration = 0.2
  const buffer = ctx.createBuffer(
    1,
    Math.ceil(ctx.sampleRate * noiseDuration),
    ctx.sampleRate
  )
  const data = buffer.getChannelData(0)
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 2
  }
  const noise = ctx.createBufferSource()
  noise.buffer = buffer
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 1800
  const noiseGain = ctx.createGain()
  noiseGain.gain.setValueAtTime(volume, now)
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + noiseDuration)
  noise.connect(filter)
  filter.connect(noiseGain)
  noiseGain.connect(ctx.destination)
  noise.start(now)

  const thump = ctx.createOscillator()
  thump.type = 'sine'
  thump.frequency.setValueAtTime(280, now)
  thump.frequency.exponentialRampToValueAtTime(60, now + 0.12)
  const thumpGain = ctx.createGain()
  thumpGain.gain.setValueAtTime(volume, now)
  thumpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.15)
  thump.connect(thumpGain)
  thumpGain.connect(ctx.destination)
  thump.start(now)
  thump.stop(now + 0.2)

  return 600
}
